import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Lock, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      nav(data.user.role === "admin" ? "/admin" : "/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FFFFFF] text-[#0A0A0A]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-black/10 grid-bg relative">
        <div className="flex items-center gap-3">
          <Radio className="text-[#0090A8]" size={28} />
          <span className="font-display font-black text-2xl tracking-tight">WEBINAR<span className="text-[#0090A8]">HUB</span></span>
        </div>
        <div>
          <div className="overline mb-4">Internal Broadcast Network</div>
          <h1 className="font-display font-black text-5xl leading-none tracking-tight mb-6">
            Watch live.<br />Ask anything.<br /><span className="text-[#0090A8]">Stay in sync.</span>
          </h1>
          <p className="text-gray-400 max-w-md leading-relaxed">
            The company command center for live webinars. Join broadcasts in real time and submit questions to presenters as they speak.
          </p>
        </div>
        <div className="overline">© {new Date().getFullYear()} WebinarHub · Employee Portal</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Radio className="text-[#0090A8]" size={24} />
            <span className="font-display font-black text-xl">WEBINARHUB</span>
          </div>
          <div className="overline mb-2">Sign In</div>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-8">Access your portal</h2>

          <label className="overline block mb-2">Employee ID / Email</label>
          <div className="relative mb-5">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              data-testid="login-identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="EMP1001 or you@company.com"
              className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm pl-10 pr-4 py-3 text-[#0A0A0A] placeholder-gray-400 outline-none transition-colors"
              required
            />
          </div>

          <label className="overline block mb-2">Password</label>
          <div className="relative mb-8">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm pl-10 pr-4 py-3 text-[#0A0A0A] placeholder-gray-400 outline-none transition-colors"
              required
            />
          </div>

          <button
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-[#0090A8] text-black hover:bg-[#00728A] font-bold rounded-sm py-3 transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <p className="text-xs text-gray-600 mt-6 leading-relaxed">
            First time here? Your initial password is your <span className="text-gray-400">Employee ID</span>. Contact your administrator for a reset.
          </p>
        </form>
      </div>
    </div>
  );
}
